import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@repo/ui/components/ui/dialog";
import type { BookingFlow, BookingPage } from "./booking-store";
import { ConsultFlow } from "./consult-flow";
import { ModeSelect } from "./mode-select";
import { ProtocolsFlow } from "./protocols-flow";
import { useBookingDialog } from "./use-booking-dialog";

export interface AppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onExited: () => void;
  initialDate?: Date;
  initialTime?: string;
  /** When set, open directly into the consult flow with this patient pre-filled. */
  initialPatientId?: string;
  /** When set, bypass mode-select and open onto this flow's first page. */
  initialFlow?: BookingFlow;
}

const PAGE_TITLE: Record<BookingPage, string> = {
  "mode-select": "New Appointment",
  "protocols.form": "Protocol Appointment",
  "protocols.slots": "Select Time Slot",
  "consult.form": "Book Consultation",
  "consult.slots": "Select Consultation Slot",
  "consult.confirm": "Confirm Consultation",
};

const PAGE_DESCRIPTION: Record<BookingPage, string> = {
  "mode-select": "Choose a booking type to get started.",
  "protocols.form": "Select a protocol to book.",
  "protocols.slots": "Pick an available time slot.",
  "consult.form": "Choose consultation type and provider.",
  "consult.slots": "Pick an available consultation slot.",
  "consult.confirm": "Review and confirm your consultation booking.",
};

export function AppointmentDialog({
  open,
  onClose,
  onExited,
  initialDate,
  initialTime,
  initialPatientId,
  initialFlow,
}: AppointmentDialogProps) {
  const { flow, page, actions } = useBookingDialog(initialFlow);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      onOpenChangeComplete={(next) => {
        if (!next) onExited();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{PAGE_TITLE[page]}</DialogTitle>
          <DialogDescription>{PAGE_DESCRIPTION[page]}</DialogDescription>
        </DialogHeader>

        {page === "mode-select" && <ModeSelect onSelect={actions.setMode} />}

        {flow === "protocols" && (
          <ProtocolsFlow
            page={page}
            initialDate={initialDate}
            initialTime={initialTime}
            onClose={onClose}
            onNext={actions.next}
            onBack={actions.back}
          />
        )}

        {flow === "consult" && (
          <ConsultFlow
            page={page}
            onClose={onClose}
            onNext={actions.next}
            onBack={actions.back}
            initialPatientId={initialPatientId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
